"use client";

import React from "react";
import { GroupingWrapper } from "../../GroupingWrapper";
import { useUniversalEntityPageCallbacksContext } from "../contexts/UniversalEntityPageCallbacksContext";
import { useUniversalEntityPageDataContext } from "../contexts/UniversalEntityPageDataContext";
import { useUniversalEntityPageUIContext } from "../contexts/UniversalEntityPageUIContext";
import type { BaseEntity } from "../../UniversalCRMTable/types";

/**
 * EntityPageTable - Renders the table for an entity page
 *
 * Consumes the entity-page contexts directly instead of receiving props.
 *
 * It deliberately hands GroupingWrapper NOTHING but the render function.
 * `useGroupingWrapperData` reads the very same five entity-page contexts and,
 * whenever it finds them, returns those values and IGNORES the props entirely —
 * so `data`/`config`/`searchTerm`/`setSearchTerm`/`filters`/`setFilters`/
 * `onExport`/`onEdit`/`onView`/`onOpenDetailModal`/`hasNextPage`/
 * `isFetchingNextPage`/`onLoadMore` were read from context here only to be
 * re-read from context two levels down. Forwarding them was dead weight, and it
 * forced this memo'd component to subscribe to the static and handlers contexts
 * for values it never rendered. The props path still exists for callers OUTSIDE
 * an entity page (e.g. SimpleTableMobileDemo).
 *
 * Memoized to prevent re-renders when parent context changes but table data hasn't.
 */
function EntityPageTableComponent<T extends BaseEntity>() {
  // One narrow read per slice this component actually renders from.
  const { dataState } = useUniversalEntityPageDataContext<T>();
  const { filterState } = useUniversalEntityPageUIContext<T>();
  const { renderSimpleTable } = useUniversalEntityPageCallbacksContext<T>();

  const { processedData, ftsIsLoading } = dataState;
  const { searchTerm } = filterState;

  return (
    <div className="h-full flex flex-col relative">
      <GroupingWrapper<T>>{renderSimpleTable}</GroupingWrapper>

      {/* Show loading overlay only while waiting for initial FTS results */}
      {/* Hide as soon as we get ANY results (even if partial), let individual row */}
      {/* loading indicators (_isLoading on each row) show the full data fetch progress */}
      {ftsIsLoading && searchTerm && searchTerm.trim().length > 0 && processedData.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/70 dark:bg-gray-900/70 rounded z-50">
          <div className="flex flex-col items-center gap-2">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <span className="text-sm text-gray-600 dark:text-gray-400">Searching...</span>
          </div>
        </div>
      )}
    </div>
  );
}

export const EntityPageTable = React.memo(EntityPageTableComponent) as React.FC<{}>;
